<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { RecordModel } from 'pocketbase'
import TurnstileWidget from '../components/TurnstileWidget.vue'
import { pb } from '../services/pocketbase'
import type { Nickname } from '../types'

const router = useRouter()
const nicknames = ref<Nickname[]>([])
const category = ref('')
const subcategory = ref('')
const nicknameId = ref('')
const customNickname = ref('')
const inviteCode = ref('')
const email = ref('')
const participantType = ref('')
const fullName = ref('')
const organization = ref('')
const phone = ref('')
const password = ref('')
const passwordConfirm = ref('')
const turnstileToken = ref('')
const loading = ref(false)
const error = ref('')
const createdUsername = ref('')
const pendingApproval = ref(false)

const OTHER_VALUE = '__other__'
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/
const CATEGORY_ORDER = [
  '金庸武侠',
  '中国古典名著',
  '经典动漫',
  '影视明星',
  '著名科学家',
  '著名经济学家',
  '计算机与人工智能',
  '地理科学',
  '体育明星',
]

const categories = computed(() =>
  [...new Set(nicknames.value.map((item) => item.category))].sort((left, right) => {
    const leftIndex = CATEGORY_ORDER.indexOf(left)
    const rightIndex = CATEGORY_ORDER.indexOf(right)
    if (leftIndex === -1 && rightIndex === -1) {
      return left.localeCompare(right, 'zh-CN')
    }
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    return leftIndex - rightIndex
  }),
)
const subcategories = computed(() =>
  [
    ...new Set(
      nicknames.value
        .filter((item) => item.category === category.value)
        .map((item) => item.subcategory),
    ),
  ].sort((left, right) => left.localeCompare(right, 'zh-CN')),
)
const availableNames = computed(() =>
  nicknames.value.filter(
    (item) =>
      item.category === category.value &&
      item.subcategory === subcategory.value,
  ),
)
const usesCustomNickname = computed(() => nicknameId.value === OTHER_VALUE)
const isCourseStudent = computed(() => participantType.value === 'student')
const needsApproval = computed(
  () => Boolean(participantType.value) && !isCourseStudent.value,
)

onMounted(async () => {
  const response = await pb.send<{ items: Nickname[] }>(
    '/api/econometrics/nicknames',
    {},
  )
  nicknames.value = response.items
})

function categoryChanged() {
  subcategory.value = ''
  nicknameId.value = ''
  customNickname.value = ''
}

function subcategoryChanged() {
  nicknameId.value = ''
  customNickname.value = ''
}

function nicknameChanged() {
  if (!usesCustomNickname.value) {
    customNickname.value = ''
  }
}

function participantChanged() {
  if (isCourseStudent.value) {
    email.value = ''
    fullName.value = ''
    organization.value = ''
    phone.value = ''
  } else {
    inviteCode.value = ''
  }
}

async function register() {
  error.value = ''
  if (!PASSWORD_PATTERN.test(password.value)) {
    error.value = '密码至少 6 位，且必须同时包含数字、小写字母和大写字母。'
    return
  }
  if (password.value !== passwordConfirm.value) {
    error.value = '两次输入的密码不一致。'
    return
  }
  if (!participantType.value) {
    error.value = '请先选择使用身份。'
    return
  }
  if (
    isCourseStudent.value &&
    !/^[A-Za-z0-9]{6}$/.test(inviteCode.value.trim())
  ) {
    error.value = '选课学生必须填写由任课教师提供的 6 位课程邀请码。'
    return
  }
  if (
    needsApproval.value &&
    (!email.value.trim() ||
      !fullName.value.trim() ||
      !organization.value.trim() ||
      !phone.value.trim())
  ) {
    error.value = '非选课人员必须填写邮箱、姓名、单位和手机号。'
    return
  }
  const normalizedCustomNickname = customNickname.value.trim().replace(/\s+/g, ' ')
  if (
    usesCustomNickname.value &&
    (normalizedCustomNickname.length < 2 || normalizedCustomNickname.length > 20)
  ) {
    error.value = '自定义化名需要填写 2 至 20 个字符。'
    return
  }
  if (!turnstileToken.value) {
    error.value = '请先完成人机验证。'
    return
  }
  loading.value = true
  try {
    const auth = await pb.send<{
      token?: string
      record: RecordModel
      pending?: boolean
    }>(
      '/api/econometrics/register',
      {
        method: 'POST',
        body: {
          invite_code: isCourseStudent.value ? inviteCode.value.trim() : '',
          email: needsApproval.value ? email.value.trim().toLowerCase() : '',
          participant_type: participantType.value,
          real_name: needsApproval.value ? fullName.value.trim() : '',
          organization: needsApproval.value ? organization.value.trim() : '',
          phone: needsApproval.value ? phone.value.trim() : '',
          nickname_id: usesCustomNickname.value ? '' : nicknameId.value,
          custom_nickname: usesCustomNickname.value
            ? normalizedCustomNickname
            : '',
          category: category.value === OTHER_VALUE ? '其他' : category.value,
          subcategory:
            subcategory.value === OTHER_VALUE ? '其他' : subcategory.value,
          password: password.value,
          turnstile_token: turnstileToken.value,
          notice_version:
            import.meta.env.VITE_NOTICE_VERSION || 'teaching-experiment-v1',
        },
      },
    )
    if (auth.pending) {
      createdUsername.value = String(auth.record.username || '')
      pendingApproval.value = true
      return
    }
    if (!auth.token) {
      throw new Error('注册响应缺少登录令牌。')
    }
    pb.authStore.save(auth.token, auth.record)
    const expanded = await pb.collection('users').getOne(String(auth.record.id), {
      expand: 'nickname',
    })
    pb.authStore.save(auth.token, expanded)
    createdUsername.value = String(expanded.username || '')
  } catch (reason) {
    const candidate = reason as { response?: { message?: string }; message?: string }
    error.value =
      candidate.response?.message ||
      candidate.message ||
      '注册失败，请检查邀请码或重新选择化名。'
  } finally {
    loading.value = false
  }
}

async function copyUsername() {
  await navigator.clipboard.writeText(createdUsername.value)
}
</script>

<template>
  <main class="register-layout">
    <header class="register-header">
      <RouterLink to="/login" class="brand-link">
        <span class="brand-mark">
          <span class="beta-symbol" aria-label="beta hat">β</span>
        </span>
        <span>
          <strong>计量客栈</strong>
          <small>Econometrics Inn · SKY Labs</small>
        </span>
      </RouterLink>
      <RouterLink to="/login">已有账号，返回登录</RouterLink>
    </header>

    <section v-if="!createdUsername" class="register-page">
      <form class="register-form" @submit.prevent="register">
        <div class="register-form-intro">
          <div>
            <p class="eyebrow">计量客栈 · Econometrics Inn</p>
            <h1>创建学习账号</h1>
          </div>
          <p>请在本页完成身份验证、化名选择和密码设置。</p>
        </div>

        <div class="register-form-steps">
          <section class="registration-step">
            <p class="step-label">步骤 1 / 3</p>
            <h2>验证课程身份</h2>
            <p class="step-guidance">
              选课学生填写6位课程邀请码，无需提交个人信息；其他人员填写联系信息并等待审核。
            </p>
            <label>
              使用身份
              <select v-model="participantType" required @change="participantChanged">
                <option value="" disabled>选择使用身份</option>
                <option value="student">选课学生</option>
                <option value="external_student">非选课学生</option>
                <option value="teacher">教师</option>
                <option value="other">其他</option>
              </select>
            </label>
          <label>
            6位课程邀请码
            <input
              v-model="inviteCode"
              :required="isCourseStudent"
              :disabled="needsApproval"
              autocomplete="off"
              minlength="6"
              maxlength="6"
              pattern="[A-Za-z0-9]{6}"
              placeholder="由任课教师提供"
            />
          </label>
            <label>
              邮箱（选课学生请勿填写）
              <input
                v-model="email"
                :required="needsApproval"
                :disabled="isCourseStudent"
                type="email"
                autocomplete="email"
                maxlength="254"
                placeholder="非选课学生填写；选课学生勿填此项"
              />
            </label>
            <div class="personal-info-grid">
            <label>
              姓名
              <input
                v-model="fullName"
                :required="needsApproval"
                :disabled="isCourseStudent"
                maxlength="80"
                autocomplete="name"
                placeholder="非选课人员填写"
              />
            </label>
            <label>
              单位
              <input
                v-model="organization"
                :required="needsApproval"
                :disabled="isCourseStudent"
                maxlength="160"
                autocomplete="organization"
                placeholder="学校或工作单位"
              />
            </label>
            <label>
              手机号
              <input
                v-model="phone"
                :required="needsApproval"
                :disabled="isCourseStudent"
                maxlength="30"
                autocomplete="tel"
                inputmode="tel"
                placeholder="用于开通联系"
              />
            </label>
            </div>
            <small class="field-note identity-note">
              非选课人员若需要开通本系统，请通过私人方式联系管理员。
            </small>
          </section>

          <section class="registration-step">
            <p class="step-label">步骤 2 / 3</p>
            <h2>选择专属化名</h2>
            <p class="step-guidance">
              请选择不包含真实姓名的化名；也可以选择“其他”并自行命名。
            </p>
            <div class="select-grid">
              <label>
                类别
                <select v-model="category" required @change="categoryChanged">
                  <option value="" disabled>选择类别</option>
                  <option v-for="item in categories" :key="item" :value="item">
                    {{ item }}
                  </option>
                  <option :value="OTHER_VALUE">其他</option>
                </select>
              </label>
              <label>
                子类别
                <select
                  v-model="subcategory"
                  required
                  :disabled="!category"
                  @change="subcategoryChanged"
                >
                  <option value="" disabled>选择子类别</option>
                  <option v-for="item in subcategories" :key="item" :value="item">
                    {{ item }}
                  </option>
                  <option :value="OTHER_VALUE">其他</option>
                </select>
              </label>
            </div>
            <label>
              可用化名
              <select
                v-model="nicknameId"
                required
                :disabled="!subcategory"
                @change="nicknameChanged"
              >
                <option value="" disabled>选择一个尚未被认领的化名</option>
                <option v-for="item in availableNames" :key="item.id" :value="item.id">
                  {{ item.display_name }}
                </option>
                <option :value="OTHER_VALUE">其他（自己命名）</option>
              </select>
            </label>
            <label v-if="usesCustomNickname">
              自定义化名
              <input
                v-model="customNickname"
                required
                minlength="2"
                maxlength="20"
                autocomplete="off"
                placeholder="请输入 2 至 20 个字符"
              />
              <small class="field-note">不能使用真实姓名，且不能与已有化名重复。</small>
            </label>
          </section>

          <section class="registration-step">
            <p class="step-label">步骤 3 / 3</p>
            <h2>设置登录密码</h2>
            <p class="step-guidance">
              密码至少6位，并且必须同时包含数字、小写字母和大写字母。
            </p>
            <div class="password-grid">
              <label>
                密码
                <input
                  v-model="password"
                  required
                  type="password"
                  autocomplete="new-password"
                  minlength="6"
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}"
                  title="至少6位，且必须同时包含数字、小写字母和大写字母"
                  placeholder="例如 Study1"
                />
              </label>
              <label>
                再次输入
                <input
                  v-model="passwordConfirm"
                  required
                  type="password"
                  autocomplete="new-password"
                  minlength="6"
                  placeholder="重复密码"
                />
              </label>
            </div>
            <TurnstileWidget @verified="turnstileToken = $event" />
          </section>
        </div>

        <div class="register-form-actions">
          <p v-if="error" class="form-error">{{ error }}</p>
          <button class="primary-button" type="submit" :disabled="loading">
            {{ loading ? '正在创建账号…' : '创建学习账号' }}
          </button>
        </div>
      </form>
    </section>

    <section v-else class="account-created">
      <div class="success-mark">✓</div>
      <p class="eyebrow">
        {{ pendingApproval ? '开通申请已提交' : '学习账号创建成功' }}
      </p>
      <h1>
        {{ pendingApproval ? '请保存你的待审核账号' : '请立即保存你的登录账号' }}
      </h1>
      <p v-if="pendingApproval">
        请通过私人方式联系课程管理员审核。审核通过前，该账号不能登录。
      </p>
      <p v-else>
        该账号只在这里完整展示一次，请妥善保存；忘记后请联系课程管理员。
      </p>
      <div class="generated-account">
        <code>{{ createdUsername }}</code>
        <button type="button" @click="copyUsername">复制账号</button>
      </div>
      <button
        class="primary-button"
        type="button"
        @click="router.push(pendingApproval ? '/login' : '/chat')"
      >
        {{ pendingApproval ? '返回登录页' : '我已保存，开始学习' }}
      </button>
    </section>
  </main>
</template>
